import React from 'react';
import clsx from 'clsx';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  children?: React.ReactNode;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  variant = 'rectangular',
  width,
  height,
  children,
}) => {
  const baseClasses = 'animate-pulse bg-rm-neutral-600/10';
  
  const variantClasses = {
    text: 'rounded-sm',
    circular: 'rounded-full',
    rectangular: 'rounded-card'
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return (
    <div
      className={clsx(
        baseClasses,
        variantClasses[variant],
        className
      )}
      style={style}
    >
      {children}
    </div>
  );
};

interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

export const SkeletonText: React.FC<SkeletonTextProps> = ({ 
  lines = 1, 
  className 
}) => {
  return (
    <div className={clsx('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          variant="text"
          height={16}
          className={clsx(
            index === lines - 1 && lines > 1 ? 'w-3/4' : 'w-full'
          )}
        />
      ))}
    </div>
  );
};

interface KPICardSkeletonProps {
  className?: string;
}

export const KPICardSkeleton: React.FC<KPICardSkeletonProps> = ({ className }) => {
  return (
    <div className={clsx('rounded-card bg-rm-bg-50 p-4', className)}>
      <div className="space-y-3">
        <SkeletonText lines={2} />
        <div className="flex items-center justify-between">
          <Skeleton height={32} width={80} />
          <Skeleton height={20} width={60} />
        </div>
      </div>
    </div>
  );
};

interface MetricCardSkeletonProps {
  className?: string;
}

export const MetricCardSkeleton: React.FC<MetricCardSkeletonProps> = ({ className }) => {
  return (
    <div className={clsx('rounded-card border border-rm-neutral-600/20 bg-white p-4', className)}>
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <SkeletonText />
          <Skeleton height={12} width={120} />
        </div>
        <div className="text-right space-y-1">
          <Skeleton height={32} width={60} />
          <Skeleton height={16} width={40} />
        </div>
      </div>
    </div>
  );
};

export default Skeleton;